/**
 * The `['table-metadata', …]` TanStack query shared by `useTableMetadata`
 * (Table Explorer, Data Profile's filter row) and imperative readers that
 * must hit the SAME cache entry — `fetchFilterColumnTypeOf`, which gives a
 * renderer-built filter WHERE (Data Profile) the column types Table
 * Explorer's generators get from their providers.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { TableMetadata } from '../../../../types/electron';
import type { FilterGroup } from '../../core/types/workspaceTypes';

export type TableMetadataScope = {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly tableName: string;
};

/** Metadata is stable; cache it for 5 minutes. */
export const TABLE_METADATA_STALE_TIME_MS = 5 * 60 * 1000;

export function tableMetadataQueryKey(scope: TableMetadataScope) {
  return ['table-metadata', scope.connectionId, scope.database, scope.schema, scope.tableName] as const;
}

/** The query function: the table's metadata over IPC, or `undefined` when unavailable. */
export async function fetchTableMetadata(scope: TableMetadataScope): Promise<TableMetadata | undefined> {
  if (!window.electronAPI?.getTableMetadata) {
    console.warn('[useTableMetadata] getTableMetadata API not available');
    return undefined;
  }

  const result = await window.electronAPI.getTableMetadata({
    connectionId: scope.connectionId,
    database: scope.database,
    schema: scope.schema,
    tableName: scope.tableName,
  });

  if (!result.success || !result.metadata) {
    console.warn('[useTableMetadata] Failed to fetch metadata:', result.error);
    return undefined;
  }

  return result.metadata;
}

/**
 * Column name → engine data type for `buildFilterWhereClause`'s
 * `columnTypeOf`, read through the shared (usually warm) table-metadata
 * cache — so a date filter takes the engine's temporal literal (Oracle
 * TO_DATE, SQL Server's seconds) exactly as in Table Explorer. `undefined`
 * when there is nothing to filter (no round trip) or the metadata is
 * unavailable (the values are then written as typed).
 */
export async function fetchFilterColumnTypeOf(
  queryClient: QueryClient,
  scope: TableMetadataScope,
  filters: readonly FilterGroup[] | undefined,
): Promise<((columnName: string) => string | undefined) | undefined> {
  if (!filters?.some((g) => g.conditions.length > 0)) return undefined;
  if (!scope.connectionId || !scope.tableName) return undefined;
  try {
    const metadata = await queryClient.fetchQuery({
      queryKey: tableMetadataQueryKey(scope),
      queryFn: () => fetchTableMetadata(scope),
      staleTime: TABLE_METADATA_STALE_TIME_MS,
    });
    if (!metadata?.columns?.length) return undefined;
    const types = new Map(metadata.columns.map((c) => [c.name, c.dataType]));
    return (columnName) => types.get(columnName);
  } catch {
    return undefined;
  }
}
